import { signInErrors } from 'const/signInErrors'
import getBusinessErrorInResponse from 'helpers/getBusinessErrorInResponse'
import isBusinessErrorData from 'helpers/isBusinessErrorData'
import getFirstFieldErrorInResponse from 'helpers/getFirstFieldErrorInResponse'
import { HTTPDataError } from 'services/helpers/HTTPDataError'
import type { AnyObject } from 'syf-component-library/typings/utils'
import { UNIVERSAL_ACTIVATION_AUTH_ERRORS } from 'const/universalActivation'
import {
  GATEWAY_CLIENT_BAD_REQUEST,
  INVALID_CLIENT_ID
} from 'const/businessErrors'
import { getSessionAuthFlow } from 'helpers/authFlows'
import { UNIVERSAL_ACTIVATION_FLOW } from 'const/constants'

interface AuthenticateHTTPErrorData extends AnyObject {
  message?: string
  data?: string
}

/**
 * Tells whether the error boundary should be triggered or not.
 * Use in the UseErrorBoundary mutation option callback
 * @param errorData obj - the error object that was thrown in the query/mutation
 * @returns boolean - whether to trigger the nearest error boundary or not
 */
function handleAuthenticateErrorCases(
  errorData: HTTPDataError<AuthenticateHTTPErrorData>
): boolean {
  // handle special cases in raw responses for when response is not ok
  const { response, data }: HTTPDataError<AuthenticateHTTPErrorData> = errorData
  const { status: httpStatusCode } = response

  switch (httpStatusCode) {
    case 422:
      // business errors
      if (isBusinessErrorData(data)) {
        const isAccountNotFound = getBusinessErrorInResponse(data, signInErrors)
        // all errors except account not found will trigger the error boundary
        return !isAccountNotFound
      }
      if (data.message === 'account not found') {
        throw new Error('no_accounts')
      }

      return true
    case 400:
      // bad request
      if (
        data.code === GATEWAY_CLIENT_BAD_REQUEST &&
        getFirstFieldErrorInResponse(data).reason === INVALID_CLIENT_ID
      ) {
        throw new Error(UNIVERSAL_ACTIVATION_AUTH_ERRORS.INVALID_CLIENT)
      }

      return true
    case 401:
      // means authorization is messed up so we need to reauthorize the session
      if (data.name === 'AUTH FAILED') {
        throw new Error('invalid_session')
      }
      break
    case 429:
      // for showing universal activation max attempts reached error screen
      if (getSessionAuthFlow() === UNIVERSAL_ACTIVATION_FLOW) {
        throw new Error(UNIVERSAL_ACTIVATION_AUTH_ERRORS.MAX_ATTEMPTS)
      }
      return true
    case 500:
    // internal server error
    case 502:
    // bad gateway
    case 503:
    // service unavailable
    case 504:
    // gateway timeout
    default:
      return true
  }
}

export default handleAuthenticateErrorCases
